<!-- ── STAMP DUTY CALCULATOR ── -->
<app-ip-shell
  [activeStep]="1"
  [showStepper]="false"
  calculatorName="Stamp Duty Calculator">

  <!-- ── FORM COLUMN ── -->
  <div formCol>
    <div class="ip-form-head">
      <div class="ip-form-head__step-meta">Stamp Duty Calculator</div>
      <h1 class="ip-form-head__title">Calculate your stamp duty</h1>
      <p class="ip-form-head__subtitle">
        Enter your property details to get an accurate SDLT breakdown based on purchase type and residency status.
      </p>
    </div>

    <div class="ip-form-body">

      <!-- Property Value -->
      <div class="ip-field">
        <div class="ip-field__head">
          <label class="ip-field__label" for="PropertyValue">Property value</label>
          <span class="ip-hint"><span class="ip-hint__dot"></span>UK avg: £300k</span>
        </div>
        <div class="ip-input">
          <span class="ip-input__prefix">£</span>
          <input
            type="text"
            id="PropertyValue"
            class="ip-input__field"
            autocomplete="off"
            placeholder="e.g. 400,000"
            [(ngModel)]="PropertyValue"
            (keyup)="PropertyValue=validation.amountWithComma(PropertyValue); calcLive()"
            (keypress)="validation.numberOnly($event)">
        </div>
      </div>

      <!-- Purchase Type -->
      <div class="ip-field">
        <div class="ip-field__head">
          <label class="ip-field__label">Purchase type</label>
        </div>
        <div class="s3-currency-wrap">
          <button
            type="button"
            class="s3-currency-btn"
            (click)="purchaseDropdownOpen=!purchaseDropdownOpen"
            [class.open]="purchaseDropdownOpen">
            <span class="s3-currency-val" [class.placeholder]="!PurchaseType">
              {{ PurchaseType ? selectedPurchaseName : 'Select purchase type' }}
            </span>
            <svg class="s3-currency-chevron" [class.rotated]="purchaseDropdownOpen" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          </button>
          <div class="s3-dropdown" *ngIf="purchaseDropdownOpen">
            <div class="s3-dropdown__list">
              <div
                *ngFor="let p of purchaseTypes"
                class="s3-dropdown__item sdlt-purchase-item"
                [class.selected]="p.value===PurchaseType"
                (click)="selectPurchaseType(p.value)">
                <span class="sdlt-purchase-name">{{ p.label }}</span>
                <span class="sdlt-purchase-desc">{{ p.desc }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Foreign Buyer -->
      <div class="ip-field">
        <div class="ip-field__head">
          <label class="ip-field__label">Foreign buyer?</label>
          <span class="ip-hint"><span class="ip-hint__dot"></span>+2% non-resident surcharge</span>
        </div>
        <div class="ip-chip-row">
          <button class="ip-chip" [class.is-active]="foreignbuyer==='1'" (click)="foreignbuyer='1'; calcLive()" type="button">Yes</button>
          <button class="ip-chip" [class.is-active]="foreignbuyer==='0'" (click)="foreignbuyer='0'; calcLive()" type="button">No</button>
        </div>
      </div>

    </div><!-- /ip-form-body -->

    <div class="ip-form-actions">
      <button class="ip-btn ip-btn--ghost" type="button" [routerLink]="['/stamp-duty-calculator/step1']">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline>
        </svg>
        Back
      </button>
      <button class="ip-btn ip-btn--accent" type="button" (click)="next()">
        Calculate SDLT
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline>
        </svg>
      </button>
    </div>
  </div><!-- /formCol -->

  <!-- ── RAIL — live SDLT preview ── -->
  <div rail>
    <div class="ip-rail-summary">
      <div class="ip-rail-summary__head">
        <div class="ip-rail-summary__loc">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle>
          </svg>
          <span class="ip-rail-summary__loc-name">{{ locationName }}</span>
        </div>
        <div class="ip-rail-summary__buyer-tag" [class.is-uk]="foreignbuyer!=='1'">
          {{ selectedPurchaseName }} &middot; {{ foreignbuyer === '1' ? 'Foreign' : 'UK' }}
        </div>
      </div>

      <div class="ip-rail-summary__body">

        <!-- Property -->
        <div class="ip-rs-section">
          <div class="ip-rs-section__head">
            <span class="ip-rs-section__label">Property</span>
          </div>
          <div class="ip-rs-row">
            <span class="ip-rs-row__key">Purchase price</span>
            <span class="ip-rs-row__val">{{ livePrice ? ('£' + (livePrice | number:'1.0-0')) : '—' }}</span>
          </div>
          <div class="ip-rs-row" *ngIf="PurchaseType">
            <span class="ip-rs-row__key">Purchase type</span>
            <span class="ip-rs-row__val">{{ selectedPurchaseName }}</span>
          </div>
        </div>

        <!-- SDLT breakdown -->
        <div class="ip-rs-section">
          <div class="ip-rs-section__head">
            <span class="ip-rs-section__label">SDLT Breakdown</span>
            <span class="ip-rs-section__total">{{ liveSdlt ? ('£' + (liveSdlt | number:'1.0-0')) : '—' }}</span>
          </div>
          <div class="ip-rs-row">
            <span class="ip-rs-row__key">Total SDLT</span>
            <span class="ip-rs-row__val">{{ liveSdlt ? ('£' + (liveSdlt | number:'1.0-0')) : '—' }}</span>
          </div>
          <div class="ip-rs-row">
            <span class="ip-rs-row__key">Effective rate</span>
            <span class="ip-rs-row__val">{{ livePrice ? ((liveRate | number:'1.2-2') + '%') : '—' }}</span>
          </div>
          <div class="ip-rs-row" *ngIf="PurchaseType==='investment'">
            <span class="ip-rs-row__key">Additional dwelling (5%)</span>
            <span class="ip-rs-row__val">{{ liveAdsSurcharge ? ('£' + (liveAdsSurcharge | number:'1.0-0')) : '—' }}</span>
          </div>
          <div class="ip-rs-row" *ngIf="foreignbuyer==='1'">
            <span class="ip-rs-row__key">Non-resident surcharge (2%)</span>
            <span class="ip-rs-row__val">{{ liveForeignSurcharge ? ('£' + (liveForeignSurcharge | number:'1.0-0')) : '—' }}</span>
          </div>
        </div>

      </div>

      <div class="ip-rail-summary__outlay">
        <div class="ip-rail-summary__outlay-label">Total stamp duty</div>
        <div class="ip-rail-summary__outlay-value">{{ liveSdlt ? ('£' + (liveSdlt | number:'1.0-0')) : '—' }}</div>
        <div class="ip-rail-summary__outlay-meta">{{ livePrice ? ((liveRate | number:'1.2-2') + '% effective rate') : 'Enter a property value' }}</div>
      </div>
    </div>
  </div><!-- /rail -->

</app-ip-shell>
